import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import {
  AuthenticationAction,
  authenticate,
  unauthenticate,
} from 'src/lib/auth/actions';
import { IAppState } from 'src/store';
import { USER_STATUS } from '../constants';
import history from 'src/lib/history';
import { handleProspectUserRedirect } from 'src/store/signup';
import { track, EVENTS } from 'src/util/track';
import User from 'src/models/User';
import AuthService from 'src/lib/auth/auth-service';
import { encodeCookie, decodeCookie } from 'src/lib/cookie-parser';
import { persistCampaignFromSSOUrl } from 'src/util/campaign.util';

// How long (ms) to show the error screen before auto-redirecting to login.
const ERROR_REDIRECT_DELAY_MS = 2500;

interface UseSSOAuthReturn {
  loading: boolean;
  error: string | null;
}

export const useSSOAuth = (): UseSSOAuthReturn => {
  // Always start in loading state — loader is visible immediately on mount.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ref to track the error-redirect timer so we can cancel it on unmount.
  const errorRedirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = useLocation();
  const dispatch: ThunkDispatch<AuthenticationAction, any, AnyAction> =
    useDispatch();

  const user = useSelector((state: IAppState) => state.auth?.user);
  const isAuthenticated = useSelector(
    (state: IAppState) => state.auth?.isAuthenticated
  );

  /**
   * Returns the `redirect` query param value, defaulting to '/account'.
   * Also persists any `product` param found inside the redirect URL.
   */
  const getRedirectPath = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectPath = urlParams.get('redirect') || '/account';

    try {
      const redirectUrl = new URL(redirectPath, window.location.origin);
      const productParam = redirectUrl.searchParams.get('product');
      if (productParam) {
        sessionStorage.setItem('sso_product_param', productParam);
      }
    } catch (e) {
      console.log('SSO: Could not parse redirect URL for product param');
    }

    return redirectPath;
  }, [location.search]);

  const handleRedirectionCondition = useCallback(
    (user: User | undefined) => {
      const redirectPath = getRedirectPath();
      console.log('SSO: Redirecting user to:', redirectPath);
      console.log('SSO: User status:', user?.status);

      if (
        user?.status === USER_STATUS.ACTIVE ||
        user?.status === USER_STATUS.INITIAL ||
        user?.status === USER_STATUS.INACTIVE || // for Reactivation Flow
        user?.status === USER_STATUS.FINAL
      ) {
        console.log('SSO: Active user - pushing to:', redirectPath);
        history.push(redirectPath);
      } else {
        console.log('SSO: Prospect user - redirecting to signup');
        dispatch(handleProspectUserRedirect());
        history.push('/signup?planType=premium&duration=3');
      }
    },
    [getRedirectPath, dispatch]
  );

  const doAuthenticate = useCallback(
    (user: User) => dispatch(authenticate(user)),
    [dispatch]
  );

  const doUnauthenticate = useCallback(
    () => dispatch(unauthenticate()),
    [dispatch]
  );

  useEffect(() => {
    const handleSSOAuth = async () => {
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      const redirectParam = urlParams.get('redirect') || '';

      // Step 1: Persist campaign code before any logout/redirect so it is
      //         never lost during session transitions. Delegated to campaign util.
      persistCampaignFromSSOUrl(location.search, redirectParam);

      // Step 2: If no token, redirect to login preserving the intended destination.
      if (!token) {
        console.warn('SSO: No token in URL — redirecting to login');
        const redirectPath = getRedirectPath();
        AuthService.logOut();
        doUnauthenticate();
        history.replace('/login', { from: redirectPath });
        return;
      }

      // Step 3: Logout any currently authenticated session first so the
      //         new SSO token is applied cleanly. We check the cookie directly
      //         rather than the Redux `isAuthenticated` state to avoid adding
      //         it as an effect dependency (which would cause a re-run loop
      //         after successful login).
      const existingCookie = decodeCookie();
      const hasExistingSession =
        existingCookie?.token &&
        existingCookie.token !== 'undefined' &&
        existingCookie.token !== 'null';
      if (hasExistingSession) {
        console.log('SSO: Existing session detected — logging out first');
        AuthService.logOut();
        doUnauthenticate();
      }

      try {
        // Step 4: Set the new bearer token from the SSO link.
        encodeCookie({ key: 'token', value: `Bearer ${token}` });

        // Step 5: Fetch the user profile with the new token.
        const authenticatedUser = await AuthService.getLoggedInUser();

        if (!authenticatedUser) {
          throw new Error('Failed to authenticate with provided token');
        }

        console.log(
          'SSO: User authenticated successfully -',
          authenticatedUser.email,
          authenticatedUser.status
        );

        doAuthenticate(authenticatedUser);

        track(EVENTS.loginAccount, {
          email: authenticatedUser.email,
          userStatus: authenticatedUser.status,
          loginType: 'sso',
        });

        setLoading(false);
      } catch (err) {
        console.error('SSO authentication failed:', err);

        AuthService.logOut();
        doUnauthenticate();

        let errorMessage = 'Authentication failed. Please try again.';
        if (err instanceof Error) {
          if (
            err.message.includes('Network Error') ||
            err.message.includes('timeout')
          ) {
            errorMessage =
              'Network error. Please check your connection and try again.';
          } else if (
            err.message.includes('401') ||
            err.message.includes('403')
          ) {
            errorMessage = 'Invalid or expired link. Please log in again.';
          }
        }

        setError(errorMessage);
        setLoading(false);

        // Auto-redirect to login after a short delay so the user can read
        // the error, while still preserving the intended destination.
        const redirectPath = getRedirectPath();
        errorRedirectTimer.current = setTimeout(() => {
          history.replace('/login', { from: redirectPath });
        }, ERROR_REDIRECT_DELAY_MS);
      }
    };

    handleSSOAuth();
    return () => {
      // Cancel the error-redirect timer if the component unmounts before it fires.
      if (errorRedirectTimer.current) {
        clearTimeout(errorRedirectTimer.current);
      }
    };
  }, [location.search, getRedirectPath, doAuthenticate, doUnauthenticate]);

  useEffect(() => {
    if (isAuthenticated && user && !loading && !error) {
      handleRedirectionCondition(user);
    }
  }, [isAuthenticated, user, loading, error, handleRedirectionCondition]);

  return { loading, error };
};
